import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Coach from './Coach';
import styles from './Coaches.module.css';
import { getAuthToken } from '../../lib/auth';
import { HOST } from '../../config/api';

const demoCoaches = [
    {
        id: 1,
        firstName: 'Maya',
        lastName: 'Kovac',
        image: '',
        bio: 'Helping athletes build calm, precise progression from foundational pool work to confident depth sessions.',
        variants: [
            { id: 101, name: 'Advanced Depth', description: 'Longer breath-hold work, threshold sets, and structured recovery.' },
            { id: 102, name: 'Pool Precision', description: 'Technique-driven pool work for cleaner execution and rhythm.' },
        ],
    },
    {
        id: 2,
        firstName: 'Tomas',
        lastName: 'Varga',
        image: '',
        bio: 'Structured coaching for athletes who want clear weekly planning, measurable progression, and cleaner execution.',
        variants: [
            { id: 201, name: 'Foundation Build', description: 'A progressive entry plan for consistency, confidence, and control.' },
            { id: 202, name: 'Competition Prep', description: 'Focused sessions for sharpening execution before peak efforts.' },
        ],
    },
    {
        id: 3,
        firstName: 'Elena',
        lastName: 'Marku',
        image: '',
        bio: 'A composed training style centered around breath control, consistency, and confidence under pressure.',
        variants: [
            { id: 301, name: 'Static Control', description: 'Breath-hold structure with calm entry, timing, and recovery discipline.' },
            { id: 302, name: 'Confidence Base', description: 'Short, repeatable sessions for building consistency without overload.' },
        ],
    },
    {
        id: 4,
        firstName: 'David',
        lastName: 'Simek',
        image: '',
        bio: 'Technique-first coaching that helps athletes sharpen form and make every training session count.',
        variants: [
            { id: 401, name: 'Technique Reset', description: 'Video-minded technique sessions focused on efficiency and repeatability.' },
            { id: 402, name: 'Pool Endurance', description: 'Volume-based pool progressions for athletes building capacity.' },
        ],
    },
    {
        id: 5,
        firstName: 'Nina',
        lastName: 'Bartos',
        image: '',
        bio: 'Competition-aware programming with equal attention to recovery, sustainability, and long-term confidence.',
        variants: [
            { id: 501, name: 'Women’s Competition Block', description: 'A structured prep cycle for refining depth, pacing, and recovery habits.' },
            { id: 502, name: 'Recovery Week', description: 'Lower-load technical sessions to maintain quality while reducing fatigue.' },
        ],
    },
    {
        id: 6,
        firstName: 'Lukas',
        lastName: 'Jelinek',
        image: '',
        bio: 'Focused on helping athletes make the jump from general training to more disciplined depth preparation.',
        variants: [
            { id: 601, name: 'Depth Foundations', description: 'Progressive sessions for athletes transitioning from pool confidence to depth structure.' },
            { id: 602, name: 'Equalization Focus', description: 'Support work centered around comfort, timing, and cleaner descent mechanics.' },
        ],
    },
    {
        id: 7,
        firstName: 'Sara',
        lastName: 'Novotna',
        image: '',
        bio: 'A modern coaching approach for athletes who want steady progress without overcomplicating the process.',
        variants: [
            { id: 701, name: 'Consistency Builder', description: 'Weekly planning for athletes who want a sustainable rhythm and clear progression.' },
            { id: 702, name: 'Pool Sprint Control', description: 'Short-format sessions that sharpen form under time pressure.' },
        ],
    },
    {
        id: 8,
        firstName: 'Martin',
        lastName: 'Horak',
        image: '',
        bio: 'High-accountability training built around rhythm, repeatability, and clearer performance feedback.',
        variants: [
            { id: 801, name: 'Advanced Pool Ladder', description: 'Higher-density sets for experienced athletes managing pace and fatigue.' },
            { id: 802, name: 'Pre-Trip Preparation', description: 'A short block for getting ready before a training camp or open-water week.' },
        ],
    },
    {
        id: 9,
        firstName: 'Petra',
        lastName: 'Liska',
        image: '',
        bio: 'Supportive entry-level coaching that keeps training approachable while building strong foundations.',
        variants: [
            { id: 901, name: 'Beginner Entry Plan', description: 'Simple, confidence-first sessions for new athletes entering structured training.' },
            { id: 902, name: 'Technique and Calm', description: 'A lighter approach built around ease, awareness, and cleaner execution.' },
        ],
    },
    {
        id: 10,
        firstName: 'Jan',
        lastName: 'Kral',
        image: '',
        bio: 'Helps athletes prepare for open-water and event-specific blocks with practical, focused programming.',
        variants: [
            { id: 1001, name: 'Open Water Readiness', description: 'Preparation work for athletes moving from pool structure into open-water conditions.' },
            { id: 1002, name: 'Performance Review Block', description: 'A coaching plan centered on feedback loops and measurable adjustments.' },
        ],
    },
];

const Coaches = ({ userInfo, userSubscriptionsUpdated, setUserSubscriptionsUpdated, setSelectedVariant, demoMode = false }) => {
    const navigate = useNavigate();
    const [coaches, setCoaches] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [subscriptionError, setSubscriptionError] = useState('');

    const handleVariantSubscribe = async (variantId) => {
        if (!userInfo) {
            navigate('/login');
            return;
        }

        setSubscriptionError('');

        const payload = {
            userId: userInfo.id,
            variantId,
        };

        try {
            const response = await fetch(HOST + '/v1/subscribe_user_to_variant', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json().catch(() => null);

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok || result?.status === 'error') {
                setSubscriptionError(result?.message || 'This plan could not be subscribed to right now. Please try again.');
                return;
            }

            setSelectedVariant?.(variantId);
            setUserSubscriptionsUpdated?.(!userSubscriptionsUpdated);
        } catch (error) {
            setSubscriptionError('This plan could not be subscribed to right now. Please check your connection and try again.');
            console.error('Failed to subscribe to training plan', error);
        }
    };

    useEffect(() => {
        let active = true;

        const fetchCoaches = async () => {
            if (demoMode) {
                setCoaches(demoCoaches);
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(HOST + '/v1/coaches', {
                    method: 'GET',
                });

                if (!response.ok) {
                    throw new Error('Coach list request failed.');
                }

                const data = await response.json();

                if (active) {
                    setCoaches(data?.result || []);
                    setLoadError('');
                }
            } catch (error) {
                if (active) {
                    setCoaches([]);
                    setLoadError('Coaches could not be loaded right now. Please try again in a moment.');
                }
            } finally {
                if (active) {
                    setIsLoading(false);
                }
            }
        };

        fetchCoaches();

        return () => {
            active = false;
        };
    }, [demoMode]);

    const coachCountLabel = useMemo(() => `${coaches.length} coach${coaches.length === 1 ? '' : 'es'} available`, [coaches]);

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div>
                    <span className={styles.kicker}>Coach discovery</span>
                    <h2>Find the training approach that fits where you are right now.</h2>
                    <p>
                        Browse coaches, compare their plans, and subscribe to the training path that matches your current focus.
                    </p>
                </div>

                <div className={styles.heroMeta}>
                    <div className={styles.metaCard}>
                        <strong>{coachCountLabel}</strong>
                        <span>{userInfo ? 'Choose a plan and subscribe directly.' : 'Sign in to subscribe and activate a plan.'}</span>
                    </div>
                </div>
            </section>

            {isLoading ? (
                <div className={styles.emptyState}>Loading coaches...</div>
            ) : loadError ? (
                <div className={styles.emptyState}>{loadError}</div>
            ) : coaches.length === 0 ? (
                <div className={styles.emptyState}>No coaches are available right now.</div>
            ) : (
                <>
                    {subscriptionError ? (
                        <div className={styles.emptyState} role="alert">{subscriptionError}</div>
                    ) : null}
                    <section className={styles.coachesGrid}>
                        {coaches.map((coach) => (
                            <Coach
                                key={coach.id}
                                firstName={coach.firstName}
                                lastName={coach.lastName}
                                image={coach.image}
                                imageUrl={coach.imageUrl}
                                bio={coach.bio}
                                variants={coach.variants || []}
                                userInfo={userInfo}
                                handleSubscribe={handleVariantSubscribe}
                            />
                        ))}
                    </section>
                </>
            )}
        </div>
    );
};

export default Coaches;
